/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { IconButton } from "@mui/material";
import { useCurrentEditor } from "@tiptap/react";
import { ButtonTooltip } from "./button-tooltip";

/**
 * Renders a single toolbar button
 * @template {Commands} C
 * @param {ToolbarButtonProps<C>} props
 */
const ToolbarButton = (props) => {
    
    const command = props?.command;
    const icon = props?.icon;
    const activeIcon = props?.activeIcon ?? icon;
    const args = props?.args ?? [];
    const activeAttribute = props?.activeAttribute;
    const tooltip = props?.tooltip;
    const activeTooltip = props?.activeTooltip;
    const style = props?.style;
    
    const { editor } = useCurrentEditor();
    const editorIsActive = editor.isActive(activeAttribute, args[0]);
    const active = activeAttribute && editorIsActive;
    const disabled = !editor.can().chain()[command](...args).run();
    const Icon = active ? activeIcon : icon;
    const title = active ? activeTooltip : tooltip;
    const color = active ? "primary" : undefined;
    // Logger.debug({title, active, disabled, activeAttribute, command, tooltip})
    
    const executeCommand = () => {
        editor.chain().focus()[command](...args).run();
    };
    
    return (
        <ButtonTooltip title={title}>
            <IconButton
                style={style}
                color={color}
                onClick={executeCommand}
                disabled={disabled}
            >
                <Icon />
            </IconButton>
        </ButtonTooltip>
    );
};
export { ToolbarButton };
/**
 * @typedef {import("@tiptap/core").ChainedCommands} ChainedCommands
 * @typedef {keyof ChainedCommands} Commands
 * @typedef {string | Record<string, unknown>} ActiveAttribute
 * @typedef {import("react").FunctionComponent} Component
 */

/**
 * @template {Commands} C
 * @typedef {Object} ToolbarButtonProps
 * @property {Component} icon
 * @property {Component=} activeIcon
 * @property {C} command
 * @property {Parameters<ChainedCommands[C]>=} args
 * @property {ActiveAttribute=} activeAttribute
 * @property {string=} tooltip
 * @property {string=} activeTooltip
 */
